import React, { useEffect, useState } from "react";
import axios from "../../services/api";
import ReactPaginate from "react-paginate";
import { format } from "date-fns";
import fileDownload from "js-file-download";

const LOW_STOCK_THRESHOLD = 5;

const StockList = () => {
  const [stock, setStock] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [maxQty, setMaxQty] = useState(0);
  const [qtyError, setQtyError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [adjustmentDate, setAdjustmentDate] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [description, setDescription] = useState("");
  const [selectedStock, setSelectedStock] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState("remove");

  const [historyItem, setHistoryItem] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferStock, setTransferStock] = useState(null);
  const [branches, setBranches] = useState([]);
  const [destinationBranchId, setDestinationBranchId] = useState("");
  const [transferQty, setTransferQty] = useState("");
  const [transferDate, setTransferDate] = useState("");
  const [transferRemarks, setTransferRemarks] = useState("");
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user.id;
  const userRole = user.role;
  const canManageStock = ["store_manager", "superadmin"].includes(userRole);
  const activeBranch = JSON.parse(localStorage.getItem("activeBranch") || "{}");

  const fetchStock = async () => {
    try {
      const res = await axios.get("/stock", {
        params: {
          page: currentPage + 1,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });

      setStock(res.data.stock || []);
      setTotalPages(res.data.totalPages || 0);
    } catch (err) {
      console.error("Error fetching stock:", err);
    }
  };

  useEffect(() => {
    fetchStock();
  }, [currentPage, itemsPerPage, searchTerm]);

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const isItemDisabled = (item) => {
    return item.item_status !== undefined && Number(item.item_status) !== 1;
  };

  const openAdjustmentModal = (stockItem) => {
    if (isItemDisabled(stockItem)) {
      alert("This item is disabled. Stock adjustment is not allowed.");
      return;
    }

    setSelectedStock(stockItem);
    setAdjustmentDate(new Date().toISOString().slice(0, 10));
    setQty("");
    setReason("");
    setOtherReason("");
    setDescription("");
    setAdjustmentType("remove");
    setMaxQty(stockItem.available_stock);
    setQtyError("");
    setShowModal(true);
  };

  const handleQtyChange = (value) => {
    setQty(value);

    const inputQty = parseInt(value, 10);

    if (adjustmentType === "remove" && inputQty > maxQty) {
      setQtyError(`Cannot remove more than ${maxQty} items.`);
    } else {
      setQtyError("");
    }
  };

  const handleAdjustmentTypeChange = (type) => {
    setAdjustmentType(type);
    setReason("");
    setOtherReason("");
    setQtyError("");

    if (type === "remove") {
      const inputQty = parseInt(qty, 10);
      if (inputQty > maxQty) {
        setQtyError(`Cannot remove more than ${maxQty} items.`);
      }
    }
  };

  const handleAdjustmentSubmit = async () => {
    const parsedQty = parseInt(qty, 10);

    if (!selectedStock) {
      alert("No stock item selected.");
      return;
    }

    if (isItemDisabled(selectedStock)) {
      alert("This item is disabled. Stock adjustment is not allowed.");
      return;
    }

    if (!adjustmentDate || !qty || !reason) {
      alert("Please fill all required fields.");
      return;
    }

    if (reason === "Other" && !otherReason.trim()) {
      alert("Please enter other reason.");
      return;
    }

    if (!parsedQty || parsedQty <= 0) {
      alert("Quantity must be greater than 0.");
      return;
    }

    if (adjustmentType === "remove" && parsedQty > maxQty) {
      alert(`You can only remove up to ${maxQty} items.`);
      return;
    }

    const finalReason = reason === "Other" ? otherReason.trim() : reason;

    try {
      await axios.post("/stock/adjust", {
        item_id: selectedStock.id,
        date: adjustmentDate,
        qty: parsedQty,
        reason: finalReason,
        description,
        user_id: userId,
        adjustment_type: adjustmentType,
      });

      alert(
        adjustmentType === "add"
          ? "Stock added successfully"
          : "Stock removed successfully"
      );

      setShowModal(false);
      fetchStock();
    } catch (err) {
      console.error("Error submitting adjustment:", err);
      alert(err.response?.data?.error || "Failed to log adjustment");
    }
  };

  const openHistory = async (stockItem) => {
    setHistoryItem(stockItem);
    setHistoryData([]);
    setHistoryLoading(true);

    try {
      const res = await axios.get(`/stock/${stockItem.id}/history`);
      setHistoryData(res.data || []);
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openTransfer = async (stockItem) => {
    if (isItemDisabled(stockItem)) return alert("Disabled items cannot be transferred.");
    if (Number(stockItem.available_stock) <= 0) return alert("No stock is available to transfer.");
    try {
      const response = await axios.get("/stock/transfer-branches");
      setBranches(response.data || []);
      setTransferStock(stockItem);
      setDestinationBranchId("");
      setTransferQty("");
      setTransferDate(new Date().toISOString().slice(0, 10));
      setTransferRemarks("");
      setShowTransferModal(true);
    } catch {
      alert("Could not load branches.");
    }
  };

  const submitTransfer = async () => {
    const amount = Number(transferQty);
    if (!destinationBranchId || !transferDate || !Number.isFinite(amount) || amount <= 0) {
      return alert("Select a destination branch, date and valid quantity.");
    }
    if (amount > Number(transferStock.available_stock)) {
      return alert(`Only ${transferStock.available_stock} units are available.`);
    }
    try {
      setTransferSubmitting(true);
      const response = await axios.post("/stock/transfer", {
        stock_id: transferStock.id,
        from_branch_id: transferStock.branch_id || activeBranch.id,
        to_branch_id: Number(destinationBranchId),
        quantity: amount,
        transfer_date: transferDate,
        remarks: transferRemarks,
      });
      alert(`Stock transferred successfully. Transfer No: ${response.data.transfer_number}`);
      setShowTransferModal(false);
      fetchStock();
    } catch (error) {
      alert(error.response?.data?.error || "Stock transfer failed.");
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleExport = async (fmt) => {
    try {
      const response = await axios.get(`/stock/export?format=${fmt}`, {
        responseType: "blob",
      });

      const filename = fmt === "excel" ? "stock.xlsx" : "stock.pdf";
      fileDownload(response.data, filename);
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export stock data");
    }
  };

  const reasonOptions =
    adjustmentType === "add"
      ? ["Lost & Found", "Return", "Other"]
      : ["Lost", "Damage", "Other"];

  return (
    <div>
      <div className="ims-page-header">
        <div>
          <h4>Stock List</h4>
          <p>Current inventory levels across all items</p>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleExport("excel")}
          >
            <i className="fas fa-file-excel mr-1"></i> Excel
          </button>

          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleExport("pdf")}
          >
            <i className="fas fa-file-pdf mr-1"></i> PDF
          </button>
        </div>
      </div>

      <div className="row mb-3 align-items-center">
        <div className="col-md-5">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(0);
            }}
            className="form-control form-control-sm"
          />
        </div>

        <div className="col-md-7 text-right">
          <div style={{ fontSize: "12px", fontWeight: 600 }}>
            <span style={{ color: "#ef4444", marginRight: "14px" }}>
              <i
                className="fas fa-circle mr-1"
                style={{ fontSize: "8px" }}
              ></i>
              Low stock ≤ {LOW_STOCK_THRESHOLD}
            </span>

            <span style={{ color: "#b91c1c" }}>
              <i
                className="fas fa-square mr-1"
                style={{ fontSize: "8px" }}
              ></i>
              Disabled item row highlighted red
            </span>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="ims-table">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Item Name</th>
              <th>Available Stock</th>
              <th>Last Updated</th>
              <th>History</th>
              {canManageStock && <th>Action</th>}
            </tr>
          </thead>

          <tbody>
            {stock.map((item, index) => {
              const disabledItem = isItemDisabled(item);
              const isLowStock =
                Number(item.available_stock) <= LOW_STOCK_THRESHOLD;

              return (
                <tr
                  key={item.id}
                  className={isLowStock && !disabledItem ? "ims-low-stock" : ""}
                  style={
                    disabledItem
                      ? {
                          background: "#fee2e2",
                          borderLeft: "4px solid #dc2626",
                        }
                      : {}
                  }
                >
                  <td>{currentPage * itemsPerPage + index + 1}</td>

                  <td>
                    <strong style={{ color: disabledItem ? "#991b1b" : "" }}>
                      {item.item_name}
                    </strong>

                    {disabledItem && (
                      <span
                        className="ml-2"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: "999px",
                          background: "#dc2626",
                          color: "#ffffff",
                        }}
                      >
                        Disabled
                      </span>
                    )}

                    {!disabledItem && isLowStock && (
                      <span className="ims-badge ims-badge-low-stock ml-2">
                        Low Stock
                      </span>
                    )}
                  </td>

                  <td>
                    <span
                      style={{
                        fontWeight: 700,
                        color: disabledItem
                          ? "#991b1b"
                          : isLowStock
                          ? "#991b1b"
                          : "#065f46",
                      }}
                    >
                      {item.available_stock}
                    </span>
                  </td>

                  <td>
                    {item.last_updated
                      ? format(new Date(item.last_updated), "dd/MM/yyyy HH:mm")
                      : "—"}
                  </td>

                  <td>
                    <button
                      className="ims-btn ims-btn-view"
                      title="View Stock History"
                      onClick={() => openHistory(item)}
                    >
                      <i className="fas fa-history"></i>
                    </button>
                  </td>

                  {canManageStock && (
                    <td>
                      <button
                        className="ims-btn ims-btn-warn"
                        title={
                          disabledItem
                            ? "Disabled item. Adjustment not allowed."
                            : "Stock Adjustment"
                        }
                        onClick={() => openAdjustmentModal(item)}
                        disabled={disabledItem}
                        style={
                          disabledItem
                            ? {
                                opacity: 0.45,
                                cursor: "not-allowed",
                                pointerEvents: "none",
                              }
                            : {}
                        }
                      >
                        <i className="fas fa-sliders-h"></i>
                      </button>
                      <button
                        className="ims-btn ims-btn-view ml-1"
                        title="Transfer to another branch"
                        onClick={() => openTransfer(item)}
                        disabled={disabledItem || Number(item.available_stock) <= 0}
                      >
                        <i className="fas fa-exchange-alt"></i>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}

            {stock.length === 0 && (
              <tr>
                <td
                  colSpan={canManageStock ? 6 : 5}
                  className="text-center text-muted py-4"
                >
                  No stock data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ReactPaginate
        pageCount={totalPages}
        onPageChange={handlePageClick}
        containerClassName="pagination justify-content-center mt-3"
        pageClassName="page-item"
        pageLinkClassName="page-link"
        activeClassName="active"
        previousLabel="← Previous"
        nextLabel="Next →"
        previousClassName="page-item"
        nextClassName="page-item"
        previousLinkClassName="page-link"
        nextLinkClassName="page-link"
        breakLabel="..."
        breakClassName="page-item"
        breakLinkClassName="page-link"
      />

      {historyItem && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.4)" }}
        >
          <div className="modal-dialog modal-lg">
            <div
              className="modal-content shadow-lg"
              style={{ borderRadius: "12px", border: "none" }}
            >
              <div
                className="modal-header"
                style={{
                  borderRadius: "12px 12px 0 0",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontWeight: 700, color: "#0f172a" }}
                >
                  <i className="fas fa-history mr-2 text-primary"></i>
                  Stock History — {historyItem.item_name}

                  <span
                    className="ml-2"
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#64748b",
                    }}
                  >
                    (Current: {historyItem.available_stock} units)
                  </span>

                  {isItemDisabled(historyItem) && (
                    <span
                      className="ml-2"
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: "999px",
                        background: "#dc2626",
                        color: "#ffffff",
                      }}
                    >
                      Disabled Item
                    </span>
                  )}
                </h5>

                <button
                  className="btn-close"
                  onClick={() => setHistoryItem(null)}
                ></button>
              </div>

              <div className="modal-body p-0">
                {historyLoading ? (
                  <div className="text-center py-4">
                    <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No transaction history found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Source</th>
                          <th>Type</th>
                          <th>Qty</th>
                          <th>Reference</th>
                          <th>Reason / Notes</th>
                          <th>Done By</th>
                        </tr>
                      </thead>

                      <tbody>
                        {historyData.map((row, i) => {
                          const isIn = row.type === "Stock In";

                          return (
                            <tr key={i}>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {row.transaction_date
                                  ? format(
                                      new Date(row.transaction_date),
                                      "dd/MM/yyyy"
                                    )
                                  : "—"}
                              </td>

                              <td>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    padding: "2px 8px",
                                    borderRadius: "6px",
                                    background:
                                      row.source === "GRN"
                                        ? "#eff6ff"
                                        : row.source === "Requisition"
                                        ? "#fff7ed"
                                        : row.source === "Stock Transfer"
                                        ? "#f5f3ff"
                                        : "#f0fdf4",
                                    color:
                                      row.source === "GRN"
                                        ? "#1d4ed8"
                                        : row.source === "Requisition"
                                        ? "#c2410c"
                                        : row.source === "Stock Transfer"
                                        ? "#6d28d9"
                                        : "#15803d",
                                  }}
                                >
                                  {row.source}
                                </span>
                              </td>

                              <td>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: isIn ? "#059669" : "#dc2626",
                                    fontSize: "12px",
                                  }}
                                >
                                  <i
                                    className={`fas fa-arrow-${
                                      isIn ? "down" : "up"
                                    } mr-1`}
                                  ></i>
                                  {row.type}
                                </span>
                              </td>

                              <td>
                                <strong
                                  style={{
                                    color: isIn ? "#059669" : "#dc2626",
                                  }}
                                >
                                  {isIn ? "+" : "-"}
                                  {row.qty}
                                </strong>
                              </td>

                              <td>
                                <span style={{ fontSize: "12px" }}>
                                  {row.reference || "—"}
                                </span>

                                {row.sub_reference && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: "#64748b",
                                    }}
                                  >
                                    <br />
                                    {row.sub_reference}
                                  </span>
                                )}
                              </td>

                              <td style={{ fontSize: "12px", color: "#64748b" }}>
                                {row.description || row.reason || "—"}
                              </td>

                              <td style={{ fontSize: "12px" }}>
                                {row.performed_by || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div
                className="modal-footer"
                style={{ borderTop: "1px solid #f1f5f9" }}
              >
                <span style={{ fontSize: "12px", color: "#64748b" }}>
                  {historyData.length} transaction
                  {historyData.length !== 1 ? "s" : ""} found
                </span>

                <button
                  className="btn btn-sm btn-secondary ml-auto"
                  onClick={() => setHistoryItem(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && selectedStock && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.4)" }}
        >
          <div className="modal-dialog">
            <div
              className="modal-content shadow-lg"
              style={{ borderRadius: "12px", border: "none" }}
            >
              <div
                className="modal-header"
                style={{
                  borderRadius: "12px 12px 0 0",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontWeight: 700, color: "#0f172a" }}
                >
                  <i className="fas fa-sliders-h mr-2 text-warning"></i>
                  Stock Adjustment — {selectedStock.item_name}
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: "13px" }}
                  >
                    Adjustment Type
                  </label>

                  <select
                    className="form-control form-control-sm"
                    value={adjustmentType}
                    onChange={(e) => handleAdjustmentTypeChange(e.target.value)}
                  >
                    <option value="remove">Remove Stock</option>
                    <option value="add">Add Stock</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: "13px" }}
                  >
                    Date
                  </label>

                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={adjustmentDate}
                    onChange={(e) => setAdjustmentDate(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: "13px" }}
                  >
                    Quantity{" "}
                    {adjustmentType === "remove" && (
                      <span className="text-muted">(max: {maxQty})</span>
                    )}
                  </label>

                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={qty}
                    min="1"
                    max={adjustmentType === "remove" ? maxQty : undefined}
                    onChange={(e) => handleQtyChange(e.target.value)}
                  />

                  {qtyError && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "12px" }}
                    >
                      {qtyError}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: "13px" }}
                  >
                    Reason
                  </label>

                  <select
                    className="form-control form-control-sm"
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      setOtherReason("");
                    }}
                  >
                    <option value="">Select reason</option>
                    {reasonOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {reason === "Other" && (
                  <div className="mb-3">
                    <label
                      className="form-label"
                      style={{ fontWeight: 600, fontSize: "13px" }}
                    >
                      Other Reason
                    </label>

                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder="Enter reason"
                    />
                  </div>
                )}

                <div className="mb-2">
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: "13px" }}
                  >
                    Description
                  </label>

                  <textarea
                    className="form-control form-control-sm"
                    rows="3"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Additional details optional"
                  />
                </div>
              </div>

              <div
                className="modal-footer"
                style={{ borderTop: "1px solid #f1f5f9" }}
              >
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button
                  className={
                    adjustmentType === "add"
                      ? "btn btn-sm btn-success"
                      : "btn btn-sm btn-primary"
                  }
                  onClick={handleAdjustmentSubmit}
                  disabled={!!qtyError || isItemDisabled(selectedStock)}
                >
                  {adjustmentType === "add" ? "Add Stock" : "Remove Stock"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTransferModal && transferStock && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content shadow-lg" style={{ borderRadius: "12px", border: "none" }}>
              <div className="modal-header">
                <h5 className="modal-title" style={{ fontWeight: 700 }}>
                  <i className="fas fa-exchange-alt mr-2 text-primary"></i>
                  Transfer Stock — {transferStock.item_name}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowTransferModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-light border py-2" style={{ fontSize: "13px" }}>
                  <strong>From:</strong> {branches.find((b) => Number(b.id) === Number(transferStock.branch_id || activeBranch.id))?.name || activeBranch.name || "Current branch"}<br />
                  <strong>Available:</strong> {transferStock.available_stock}
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>To Branch *</label>
                  <select className="form-control form-control-sm" value={destinationBranchId} onChange={(e) => setDestinationBranchId(e.target.value)}>
                    <option value="">Select destination branch</option>
                    {branches.filter((b) => Number(b.id) !== Number(transferStock.branch_id || activeBranch.id)).map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>Transfer Date *</label>
                    <input type="date" className="form-control form-control-sm" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>Quantity * (max {transferStock.available_stock})</label>
                    <input type="number" step="0.01" min="0.01" max={transferStock.available_stock} className="form-control form-control-sm" value={transferQty} onChange={(e) => setTransferQty(e.target.value)} />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>Remarks</label>
                  <textarea className="form-control form-control-sm" rows="3" maxLength="500" value={transferRemarks} onChange={(e) => setTransferRemarks(e.target.value)} placeholder="Optional transfer note" />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-secondary" onClick={() => setShowTransferModal(false)}>Cancel</button>
                <button className="btn btn-sm btn-primary" disabled={transferSubmitting} onClick={submitTransfer}>
                  {transferSubmitting ? "Transferring..." : "Transfer Stock"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockList;
